import { useEffect, useState } from 'react';
import { Star } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  getTickers, peekTickers, subscribe, addLocal, removeLocal,
} from '@/lib/portfolioTickersCache';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * WatchlistStarButton — reusable ★ toggle for standalone SEO pages
 * (/founder-led, /ipo-watch, /warrants) that don't sit inside
 * Dashboard's shared watchlist state.
 *
 * Round P141·6 (Feb 2026) — Now backed by the shared
 * `portfolioTickersCache` so a page with N rows fires ONE
 * `/api/portfolio` fetch instead of N. Previously the 441-row
 * Warrant Screener queued 441 concurrent requests and stars painted
 * gold one at a time over 20+ seconds; now they all light up on the
 * first cache resolve.
 *
 * Silently no-ops when the user isn't logged in.
 */
export default function WatchlistStarButton({ ticker, size = 'w-4 h-4' }) {
  // Initialize from the cache synchronously — if we've already
  // fetched the portfolio on this page, the star paints gold on
  // the FIRST render instead of after a round-trip.
  const initialSet = peekTickers();
  const [inList, setInList] = useState(initialSet ? initialSet.has(ticker) : false);
  const [loaded, setLoaded] = useState(!!initialSet);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getTickers().then((set) => {
      if (cancelled) return;
      setInList(set.has(ticker));
      setLoaded(true);
    });
    // Subscribe so any add/remove elsewhere (or another star's
    // mutation) repaints this instance without a refetch.
    const unsub = subscribe(() => {
      const set = peekTickers();
      if (set) setInList(set.has(ticker));
    });
    return () => { cancelled = true; unsub(); };
  }, [ticker]);

  const toggle = async (e) => {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      if (inList) {
        await axios.delete(`${API}/portfolio/${ticker}`, { withCredentials: true });
        removeLocal(ticker);
        setInList(false);
        toast.success(`${ticker} removed from watchlist`);
        window.dispatchEvent(new CustomEvent('watchlist-auto-removed', { detail: { ticker } }));
        window.dispatchEvent(new Event('watchlist-changed'));
        window.dispatchEvent(new Event('portfolio-changed'));
      } else {
        await axios.post(`${API}/portfolio`, { ticker }, { withCredentials: true });
        addLocal(ticker);
        setInList(true);
        toast.success(`${ticker} added to watchlist`);
        window.dispatchEvent(new CustomEvent('watchlist-auto-added', { detail: { ticker } }));
        window.dispatchEvent(new Event('watchlist-changed'));
        window.dispatchEvent(new Event('portfolio-changed'));
      }
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error('Sign in to save to your watchlist');
      } else {
        toast.error('Could not update watchlist');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={!loaded || busy}
      className="p-1 rounded hover:bg-muted/40 transition-colors disabled:opacity-40"
      data-testid={`watchlist-star-${ticker}`}
      title={inList ? 'Remove from watchlist' : 'Add to watchlist'}
      aria-label={inList ? `Remove ${ticker} from watchlist` : `Add ${ticker} to watchlist`}
    >
      <Star className={`${size} transition-colors ${inList ? 'fill-yellow-500 text-yellow-500' : 'text-muted-foreground/40 hover:text-muted-foreground'}`} />
    </button>
  );
}
